import { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Paper,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useDispatch } from 'react-redux';
import { showNotification } from '../../store/Reducers/NotificationSnackbarSlice';
import { WarehouseService } from '../../api/services/warehouseService';
import { WarehouseResponse } from '../../types/api/response/warehouseresponse';
import {
  CreateWarehouseRequest,
  UpdateWarehouseRequest,
} from '../../types/api/request/warehouserequest';

type SlideOverMode = 'add' | 'edit';

const emptyForm = {
  code: '',
  display_name: '',
  address: '',
  latitude: '',
  longitude: '',
  is_active: true,
};

export const WarehouseManagementPage = () => {
  const dispatch = useDispatch();
  const [warehouses, setWarehouses] = useState<WarehouseResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [slideOverOpen, setSlideOverOpen] = useState(false);
  const [slideOverMode, setSlideOverMode] = useState<SlideOverMode>('add');
  const [editingWarehouse, setEditingWarehouse] = useState<WarehouseResponse | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [confirmDeactivate, setConfirmDeactivate] = useState<WarehouseResponse | null>(null);

  const fetchWarehouses = useCallback(async () => {
    setLoading(true);
    try {
      const data = await WarehouseService.listWarehouses(false);
      setWarehouses(data);
    } catch (e: any) {
      dispatch(
        showNotification({
          message: e?.response?.data?.message ?? 'Failed to load warehouses.',
          severity: 'error',
        }),
      );
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    fetchWarehouses();
  }, [fetchWarehouses]);

  const openAdd = () => {
    setSlideOverMode('add');
    setEditingWarehouse(null);
    setForm(emptyForm);
    setSlideOverOpen(true);
  };

  const openEdit = (w: WarehouseResponse) => {
    setSlideOverMode('edit');
    setEditingWarehouse(w);
    setForm({
      code: w.code,
      display_name: w.display_name,
      address: w.address ?? '',
      latitude: String(w.latitude),
      longitude: String(w.longitude),
      is_active: w.is_active,
    });
    setSlideOverOpen(true);
  };

  const handleSubmit = async () => {
    const missingFields: string[] = [];
    if (slideOverMode === 'add' && !form.code.trim()) missingFields.push('Warehouse Code');
    if (!form.display_name.trim()) missingFields.push('Display Name');
    if (!form.address.trim()) missingFields.push('Address');
    if (!form.latitude.trim()) missingFields.push('Latitude');
    if (!form.longitude.trim()) missingFields.push('Longitude');
    if (missingFields.length > 0) {
      dispatch(
        showNotification({
          message: `Required fields are missing: ${missingFields.join(', ')}.`,
          severity: 'error',
        }),
      );
      return;
    }

    setSaving(true);
    try {
      if (slideOverMode === 'add') {
        const body: CreateWarehouseRequest = {
          code: form.code,
          display_name: form.display_name,
          address: form.address,
          latitude: parseFloat(form.latitude),
          longitude: parseFloat(form.longitude),
          is_active: form.is_active,
        };
        await WarehouseService.createWarehouse(body);
        dispatch(
          showNotification({ message: 'Warehouse created successfully.', severity: 'success' }),
        );
      } else {
        const body: UpdateWarehouseRequest = {
          display_name: form.display_name,
          address: form.address,
          latitude: parseFloat(form.latitude),
          longitude: parseFloat(form.longitude),
          is_active: form.is_active,
        };
        await WarehouseService.updateWarehouse(editingWarehouse!.id, body);
        dispatch(showNotification({ message: 'Warehouse updated.', severity: 'success' }));
      }
      setSlideOverOpen(false);
      fetchWarehouses();
    } catch (e: any) {
      dispatch(
        showNotification({
          message: e?.response?.data?.message ?? 'Failed to save warehouse.',
          severity: 'error',
        }),
      );
    } finally {
      setSaving(false);
    }
  };

  const handleStatusClick = (w: WarehouseResponse) => {
    if (w.is_active) {
      setConfirmDeactivate(w);
    } else {
      toggleStatus(w, true);
    }
  };

  const toggleStatus = async (w: WarehouseResponse, newStatus: boolean) => {
    try {
      await WarehouseService.updateWarehouse(w.id, { is_active: newStatus });
      fetchWarehouses();
    } catch (e: any) {
      dispatch(
        showNotification({
          message: e?.response?.data?.message ?? 'Failed to update status.',
          severity: 'error',
        }),
      );
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="flex-start" mb={2}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openAdd}>
          Add Warehouse
        </Button>
      </Box>

      {loading && warehouses.length === 0 && (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      )}

      {!loading && warehouses.length === 0 && (
        <Box py={8} textAlign="center" color="text.secondary">
          <Typography variant="h6">No warehouses found</Typography>
          <Typography variant="body2">Create a warehouse to get started.</Typography>
        </Box>
      )}

      {warehouses.length > 0 && (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Code</TableCell>
                <TableCell>Address</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {warehouses.map((w) => (
                <TableRow key={w.id}>
                  <TableCell>{w.display_name}</TableCell>
                  <TableCell>
                    <code>{w.code}</code>
                  </TableCell>
                  <TableCell>{w.address ?? '—'}</TableCell>
                  <TableCell>
                    <Chip
                      label={w.is_active ? 'Active' : 'Inactive'}
                      color={w.is_active ? 'success' : 'default'}
                      size="small"
                      onClick={() => handleStatusClick(w)}
                      sx={{ cursor: 'pointer' }}
                    />
                  </TableCell>
                  <TableCell>
                    <Button size="small" onClick={() => openEdit(w)}>
                      Edit
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add / Edit modal */}
      <Dialog open={slideOverOpen} onClose={() => setSlideOverOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{slideOverMode === 'add' ? 'Add Warehouse' : 'Edit Warehouse'}</DialogTitle>
        <DialogContent dividers>
          <Box display="flex" flexDirection="column" gap={2} pt={1}>
            <TextField
              label="Warehouse Code"
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value })}
              disabled={slideOverMode === 'edit'}
              required
              helperText={
                slideOverMode === 'add'
                  ? 'Short unique identifier (e.g. WH-SUVA). Cannot be changed after creation.'
                  : undefined
              }
              fullWidth
            />
            <TextField
              label="Display Name"
              value={form.display_name}
              onChange={(e) => setForm({ ...form, display_name: e.target.value })}
              required
              fullWidth
            />
            <TextField
              label="Address"
              value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
              required
              fullWidth
            />
            <TextField
              label="Latitude"
              type="number"
              value={form.latitude}
              onChange={(e) => setForm({ ...form, latitude: e.target.value })}
              required
              fullWidth
            />
            <TextField
              label="Longitude"
              type="number"
              value={form.longitude}
              onChange={(e) => setForm({ ...form, longitude: e.target.value })}
              required
              fullWidth
            />
            <FormControlLabel
              control={
                <Switch
                  checked={form.is_active}
                  onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setSlideOverOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSubmit} disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Deactivate confirmation */}
      <Dialog open={!!confirmDeactivate} onClose={() => setConfirmDeactivate(null)}>
        <DialogTitle>Deactivate Warehouse</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Deactivating this warehouse will hide it from the Collection Point Management warehouse
            selector. Its collection points and bays will remain in the database. Continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeactivate(null)}>Cancel</Button>
          <Button
            color="warning"
            variant="contained"
            onClick={() => {
              if (confirmDeactivate) toggleStatus(confirmDeactivate, false);
              setConfirmDeactivate(null);
            }}
          >
            Deactivate
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
